import { CampaignFormData, AdgroupFormData, IOS14Type, PromotionFlowType } from '@tt4b/ttam-enums';
import { Ref, computed, ref } from '@vue/composition-api';
import {
  ValidationError,
  useAllowlist,
  useStdCampaignIs,
  useStdAdgroupCbo,
  useSplitTestEnabled,
} from '@tt4b/ttam-hooks';
import { i18n } from './../../../i18n';
import { createSharedComposable } from '@vueuse/core';

export function usePropsData(opt: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  hasShop: Ref<boolean>;
  hasShowcase: Ref<boolean>;
  isCopy: Ref<boolean>;
  isEdit: Ref<boolean>;
}) {
  const { campaignData, adgroupData, isCopy, isEdit } = opt;
  const clickKey = 'module_ad_group_ad_type_click';
  const showKey = 'module_ad_group_ad_type_impression';
  const standard = {
    id: PromotionFlowType.STANDARD,
    title: i18n.t('module_ad_type_standards'),
    desc: i18n.t('module_ad_type_standards_desc'),
    trackProps: {
      'data-tea-click': clickKey,
      'data-tea-expose': showKey,
      'data-tea-ad_type': 'standard ads',
    },
    order: 1,
  };
  const live = {
    id: PromotionFlowType.LIVE,
    title: i18n.t('module_ad_type_live'),
    desc: i18n.t('module_ad_type_live_desc'),
    trackProps: {
      'data-tea-click': clickKey,
      'data-tea-expose': showKey,
      'data-tea-ad_type': 'live ads',
    },
    order: 2,
  };
  const cla = {
    id: PromotionFlowType.CLA,
    title: i18n.t('module_ad_type_mars'),
    desc: i18n.t('module_ad_type_mars_desc'),
    showKey,
    clickKey,
    trackProps: {
      'data-tea-click': clickKey,
      'data-tea-expose': showKey,
      'data-tea-ad_type': 'catalog listing ads',
    },
    order: 3,
  };
  const psa = {
    id: PromotionFlowType.PSA,
    title: i18n.t('module_ad_type_psa'),
    desc: i18n.t('module_ad_type_psa_desc'),
    trackProps: {
      'data-tea-click': clickKey,
      'data-tea-expose': showKey,
      'data-tea-ad_type': 'product shopping ads',
    },
    order: 4,
  };

  const { isCLAWhite, isProductSalesCloseLoopWhite, isPsaWhite } = useAllowlist();
  const { isProductSalesOpenLoop, isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: campaignData });
  const { isCBO } = useStdAdgroupCbo({ campaignFormData: campaignData, adgroupFormData: adgroupData });
  const { isSplitTestEnabled } = useSplitTestEnabled({ campaignData, adgroupData });

  const data = computed(() => {
    const selectedItem = [standard, live, cla, psa].find(item => item.id === adgroupData.value.promotionFlowType);
    if (isCopy.value) {
      return [selectedItem];
    }

    const isIos14 = adgroupData.value.targetDeviceVersion === IOS14Type.ABOVE;
    if (isIos14 || isCBO.value || isSplitTestEnabled.value) {
      return [standard];
    }

    const displayList = [standard];
    const isSelectedIndustryType = Boolean(campaignData.value.industryTypes?.length);

    // 1） user有闭环白名单，就有LIVE
    if (isProductSalesCloseLoopWhite.value && !isProductSalesOpenLoop.value) {
      displayList.push(live);
    }

    if (isCLAWhite.value && !isSelectedIndustryType && !isProductSalesCloseLoop.value) {
      displayList.push(cla);
    }

    // 闭环 && 开白PSA，则有PSA卡片选择
    if (isPsaWhite.value && isProductSalesCloseLoop.value) {
      displayList.push(psa);
    }

    /**
     * 编辑的时加上已经选择的, 保证正确的展示
     * 避免例如 shop 长度变化时回显错误
     */
    if (isEdit.value) {
      if (!displayList.find(item => item.id === selectedItem.id)) {
        displayList.push(selectedItem);
        displayList.sort((o1, o2) => o1.order - o2.order);
      }
    }
    return displayList;
  });
  return { data };
}

export const useRules = createSharedComposable(function useRules(campaignData: Ref<CampaignFormData>) {
  const { isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: campaignData });
  const moduleName = isProductSalesCloseLoop.value ? i18n.t('adgroup_shop_ad_type') : i18n.t('adgroup_ad_type_card');
  return {
    adTypeValidator: computed(() => [
      {
        trigger: 'change',
        validator: (rule, value, callback, formData: AdgroupFormData) => {
          if (!formData.promotionFlowType) {
            return callback(
              new ValidationError({
                messageI18nKey: 'adgroup_ad_type_empty',
                message: i18n.t('adgroup_ad_type_empty'),
                moduleName,
                subModuleName: moduleName,
              }),
            );
          }
          return callback();
        },
      },
    ]),
  };
});
